import React, { useState, useEffect } from 'react';
import { Trophy, Users, ChevronRight, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiGet } from '../../utils/api';

const LeaderboardWidget = () => {
  const [rankings, setRankings] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const response = await apiGet('/leaderboards/my_rankings');
        if (response.ok) {
          const data = await response.json();
          setRankings(data.rankings || []);
          setPendingCount(data.pending_invitations || 0);
        }
      } catch (err) {
        console.error('Error fetching leaderboard rankings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRankings();
  }, []);

  if (loading) return null;
  if (rankings.length === 0 && pendingCount === 0) return null;

  return (
    <section className="rounded-2xl border border-gray-200/80 dark:border-gray-800 bg-white dark:bg-gray-900/40">
      <header className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Trophy size={14} className="text-blue-600 dark:text-blue-400" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
            My Leaderboards
          </span>
        </div>
        <Link
          to="/dashboard/leaderboards"
          className="inline-flex items-center gap-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          View all <ChevronRight size={12} />
        </Link>
      </header>

      <div className="p-3">
        {pendingCount > 0 && (
          <Link
            to="/dashboard/leaderboards"
            className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 px-4 py-2.5 rounded-xl mb-2.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <Bell size={14} className="text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-blue-700 dark:text-blue-300 font-rasa">
              {pendingCount} pending invitation{pendingCount > 1 ? 's' : ''}
            </span>
          </Link>
        )}

        <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5">
          {rankings.slice(0, 5).map(lb => (
            <Link
              key={lb.id}
              to="/dashboard/leaderboards"
              className="rounded-lg border border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/40 px-2.5 py-2 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors"
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className={`inline-flex items-center justify-center h-5 min-w-[1.75rem] px-1 rounded text-[10.5px] font-bold ${
                  lb.my_rank === 1 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                  lb.my_rank === 2 ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' :
                  lb.my_rank === 3 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                  'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {lb.my_rank ? `#${lb.my_rank}` : '–'}
                </span>
                <span className="text-[12.5px] font-bold text-blue-600 dark:text-blue-400 font-rasa">
                  {lb.my_total}<span className="text-[9.5px] text-gray-400 dark:text-gray-500 font-normal ml-0.5">pts</span>
                </span>
              </div>
              <p className="text-[12.5px] font-medium text-gray-800 dark:text-gray-200 font-rasa truncate leading-tight">{lb.name}</p>
              <p className="text-[10.5px] text-gray-400 dark:text-gray-500 inline-flex items-center gap-1">
                <Users size={9} /> {lb.member_count} members
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LeaderboardWidget;
